import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  PausedJobMoveDecision,
  usePausedJobMoveGate,
} from "@/contexts/PausedJobMoveGateContext";
import { useCurrentCompanyId } from "@/hooks/useCurrentCompanyId";
import { useCurrentJobId } from "@/hooks/useCurrentJobId";
import { useCelebrateStafferPetAward } from "@/hooks/useStafferPets";
import { scheduledAgentMessagesQueryKey } from "@/hooks/queries/useGetScheduledAgentMessages";
import { useSetAppliedSnapshotForCandidacy } from "@/hooks/sets/useSetAppliedSnapshotForCandidacy";
import { useSetCandidacyInAllLists } from "@/hooks/sets/useSetCandidacyInAllLists";
import { guacHttpClient } from "@/lib/http";
import {
  DashboardData,
  HttpResponse,
  UpdateCandidacyRequest,
  UpdateCandidacyResponse,
} from "@/types";

export function useUpdateCandidacy() {
  const celebrateStafferPetAward = useCelebrateStafferPetAward();
  const pausedJobMoveGate = usePausedJobMoveGate();
  const currentCompanyId = useCurrentCompanyId();
  const currentJobId = useCurrentJobId();
  const queryClient = useQueryClient();
  const setAppliedSnapshotForCandidacy = useSetAppliedSnapshotForCandidacy();
  const setCandidacyInAllLists = useSetCandidacyInAllLists();

  const mutation = useMutation({
    mutationFn: async ({
      candidacyId,
      updateCandidacyRequest,
    }: {
      candidacyId: string;
      updateCandidacyRequest: UpdateCandidacyRequest;
    }) => {
      const path = `companies/${currentCompanyId}/jobs/${currentJobId}/candidacies/${candidacyId}`;
      const res = await guacHttpClient.patch<
        UpdateCandidacyResponse,
        HttpResponse<UpdateCandidacyResponse>,
        UpdateCandidacyRequest
      >(path, updateCandidacyRequest);

      return res.data;
    },
    onSuccess: (data, variables) => {
      const newStatus = variables.updateCandidacyRequest.status;

      // A submit coin, when guac awarded one for this move.
      celebrateStafferPetAward(data.stafferPetAward);

      setCandidacyInAllLists({
        candidacyId: variables.candidacyId,
        owners: data.candidacyOwners,
        stafferAgentActive: data.stafferAgentActive,
      });

      // A takeover or a status move retires the agent's queued follow-ups, so
      // the preview would otherwise sit on a dead chain until its next poll.
      if (!data.stafferAgentActive || newStatus !== undefined) {
        void queryClient.invalidateQueries({
          queryKey: scheduledAgentMessagesQueryKey(variables.candidacyId),
        });
      }

      if (newStatus === undefined) return;

      queryClient.setQueriesData<DashboardData>(
        { queryKey: ["dashboard"] },
        (data) =>
          data
            ? {
                ...data,
                jobs: data.jobs.map((j) =>
                  j.id === currentJobId
                    ? {
                        ...j,
                        candidacies: j.candidacies.map((c) =>
                          c.id === variables.candidacyId
                            ? { ...c, status: newStatus }
                            : c,
                        ),
                      }
                    : j,
                ),
              }
            : data,
      );

      setAppliedSnapshotForCandidacy({
        candidacyId: variables.candidacyId,
        fromStatus: variables.updateCandidacyRequest.fromStatus,
        toStatus: newStatus,
      });
    },
  });

  /**
   * Every surface that moves a candidacy comes through here, so the parked-job
   * gate wraps `mutate` rather than each call site. Cancelling means the move
   * never fires; the mutation's own callbacks are left untouched otherwise.
   */
  const mutate: typeof mutation.mutate = (variables, options) => {
    if (!pausedJobMoveGate) {
      mutation.mutate(variables, options);
      return;
    }

    void pausedJobMoveGate
      .requestDecision({
        candidacyId: variables.candidacyId,
        fromStatus: variables.updateCandidacyRequest.fromStatus,
        jobId: currentJobId,
        toStatus: variables.updateCandidacyRequest.status,
      })
      .then((decision) => {
        // The gate has already marked the job active if that was chosen.
        if (decision === PausedJobMoveDecision.CANCEL) return;
        mutation.mutate(variables, options);
      });
  };

  return { ...mutation, mutate };
}
